<template>
  <div :class="['h-full relative bg-black', isCard ? 'is-card' : '']" :style="cssVars">
    <div v-if="isCard && $route.query.backFromCard" class="mock-card w-full h-full absolute top-0 left-0 p-safe-wrap bg-ion-body"></div>
    <router-view v-slot="{ Component, route }">
      <Transition :name="route.meta.computedTransition" @after-leave="onAnimationFinished" @before-leave="onAnimationStart">
        <div
          :key="route.path"
          :class="[
            'w-full h-full absolute top-0 left-0 card-wrap bg-ion-body',
            queryCardCss,
          ]"
        >
          <component :is="Component" />

          <!-- Tab menu -->
          <div
              id="tab-menu"
              :class="[
                'p-safe-wrap-b w-full absolute bottom-0 left-0 z-10 backdrop-blur',
                $route.meta.hideChrome || $route.query.back || $route.query.backFromCard ? 'hidden' : '',
              ]"
          >
            <div class="w-full pt-1 -mb-10 pb-12 border-t border-gray-200/50 dark:border-gray-800 bg-ion-body-70 dark:bg-zinc-800/80">
              <div id="pre-tab-bar-slot" />

              <div class="flex w-full" @click="vibrate()">
                <router-link
                  v-for="page of getPages()" :key="page.route"
                  :to="{ name: page.route }"
                  :class="['flex-1 flex flex-col items-center', isActive(page) ? 'text-primary' : 'text-gray-400']"
                >
                  <ZeitIcon :name="page.icon.name" :fill="page.icon.fill" class="w-5 my-1" />
                  <div class="font-medium" style="font-size: .7rem;">{{ $t(page.label) }}</div>
                </router-link>
              </div>
            </div>
          </div>

        </div>
      </Transition>
    </router-view>
  </div>
</template>

<style scoped>
  div.m-safe-wrap {
    margin: var(--safe-wrap-t) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
  }
  div.p-safe-wrap {
    padding: var(--safe-wrap-t) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
  }
  div.p-safe-wrap-b {
    padding-bottom: env(safe-area-inset-bottom);
  }

  /* Route transitions - card show and hide */

  .show-card-enter-active,
  .show-card-leave-active,
  .hide-card-enter-active,
  .hide-card-leave-active {
    transition: all .5s cubic-bezier(.2, .5, 0, 1);
  }

  .show-card-enter-active,
  .hide-card-leave-active,
  .is-card .card-wrap {
    z-index: 50;
    border-radius: .75rem;
    overflow: hidden;
  }

  .show-card-leave-active,
  .hide-card-enter-active,
  .is-card .mock-card {
    z-index: 40;
    border-radius: .75rem;
    overflow: hidden;
  }

  .show-card-enter-from,
  .hide-card-leave-to {
    transform: translateY(100%);
  }

  .show-card-enter-to,
  .hide-card-leave-from,
  .is-card .card-wrap {
    transform: translateY(calc(var(--safe-wrap-t) + 5px));
    z-index: 50;
  }

  .show-card-leave-to,
  .hide-card-enter-from,
  .is-card .mock-card {
    transform: scale(.93) translateY(calc(var(--safe-wrap-t) - 4%));
    opacity: .85;
  }


  /* Route transitions - slide left and right */
  .slide-left-enter-active,
  .slide-left-leave-active {
    transition: transform .5s ease, opacity 1s ease;
  }

  .slide-right-enter-active,
  .slide-right-leave-active {
    transition: transform .3s ease, opacity .2s ease;
  }

  .slide-left-enter-active,
  .slide-right-leave-active {
    z-index: 10
  }

  .slide-left-enter-from,
  .slide-right-leave-to {
    transform: translateX(100%);
  }

  .slide-left-leave-to,
  .slide-right-enter-from {
    transform: translateX(-30%);
  }

</style>


<script lang="ts">
  import { defineComponent } from 'vue';

  import { Style } from '@capacitor/status-bar';

  import ZeitIcon from '@/components/ui/ZeitIcon.vue';
  import { updateIosBar, updateAndroidBar } from '@/globals/statusbar';

  import { lightImpact } from '@/globals/haptics';
  import { isPlatform } from '@ionic/vue';
  import { session } from '@/globals/session';

  interface Page {
    label: String;
    route: String;
    icon: {name: String, fill: String};
    isVisible?: () => Boolean;
  }

  const PAGES = [
    {
      label: 'message.time_tracking',
      route: 'time-tracking',
      icon: { name: 'Clock', fill: 'solid' },
    },
    {
      label: 'message.report',
      route: 'mobile-reports:employees',
      // route: 'account',
      icon: { name: 'CalendarDays', fill: 'solid' },
    },
    {
      label: 'message.absences',
      route: 'my-absences',
      icon: { name: 'HomeModern', fill: 'solid' },
      isVisible: () => Boolean(session.account && session.account.permissions.indexOf('absence-applications:view') > -1),
    },
  ];

  export default defineComponent({
    components: {
      ZeitIcon,
    },
    computed: {
      cssVars() {
        return {
          '--safe-wrap-t': 'env(safe-area-inset-top, 0rem)',
        }
      },
    },
    watch: {
      async $route(to, from) {
        if (to.query.card) {
          this.queryCardCss = 'show-card-leave-active show-card-leave-to';
          this.updateStatusBar(true);
          lightImpact();
        } else if (from.query.card) {
          this.queryCardCss = 'hide-card-enter-active hide-card-enter-to';
          this.updateStatusBar(false);
          lightImpact();
          await new Promise((resolve) => setTimeout(() => resolve(), 500));
          this.queryCardCss = '';
        }
      }
    },
    data() {
      return {
        isCard: this.displayCard(),
        queryCardCss: '',
      }
    },
    methods: {
      isActive(page: Page): boolean {
        return this.$route.name?.toString().indexOf(page.route) == 0;
      },
      getPages(): Array<Page> {
        return PAGES.filter(page => !page.isVisible || page.isVisible());
      },
      updateStatusBar(isCard: boolean) {
        if (isCard) {
          updateIosBar({}, Style.Dark);
          updateAndroidBar({}, Style.Dark);
        } else {
          updateIosBar({});
          updateAndroidBar({});
        }
      },
      displayCard() {
        return !!this.$route.query.backFromCard || !!this.$route.query.card;
      },
      onAnimationFinished() {
        if (this.displayCard()) this.isCard = true;
      },
      onAnimationStart() {
        const willBeCard = this.displayCard();
        this.updateStatusBar(willBeCard);
        if (!willBeCard) this.isCard = false;
        if (willBeCard) lightImpact();
      },
      vibrate() {
        lightImpact();
      },
    },
    mounted() {
      this.updateStatusBar(this.isCard);
    },
});
</script>
